/* eslint-disable camelcase */
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { dateFormat, parseSecondsToHM, parseSecondsToHMText } from '@nx-smartmonkey/shared/helpers';
import * as XLSX from 'xlsx';

import { CUSTOM_FIELD_COLUMN_ID_PREFIX } from '../../constants';
import { DriversTableColumn, DriversTableColumnKeys } from '../../context/driver/tableColumns';
import { Driver } from '../../domain/driver/Driver';
import { Project } from '../../domain/project/Project';
import { intl } from '../IntGlobalProvider';
import { kmToSM } from '../Units';
import { ExcelEntryFormatter, customFieldToFormatter, jsonToExcel } from './Formatters';
import { parseStringArray, parseTimewindowToExcel } from './Parsers';

const getDriverFormatters = ({
  showRootParams = false,
  parsedTableColumns,
  project,
}: {
  showRootParams?: boolean;
  parsedTableColumns: Record<DriversTableColumnKeys, boolean>;
  project: Project;
}): ExcelEntryFormatter[] => {
  const volumeIn = project.units!.volume ?? ``;
  const weightIn = project.units!.weight ?? ``;
  const distanceIn = project.units!.distance ?? ``;
  const formatters: ExcelEntryFormatter[] = [];

  if (parsedTableColumns.label) {
    formatters.push({
      header: intl.formatMessage({ id: `routes.excel.columns.name` }),
      id: `label`,
      formatter: (driver: Driver) => driver.label,
    });
  }

  if (parsedTableColumns.external_id) {
    formatters.push({
      header: intl.formatMessage({ id: `routes.excel.columns.external_id` }),
      id: `external_id`,
      formatter: (driver: Driver) => driver.external_id,
    });
  }

  if (parsedTableColumns.start_location) {
    formatters.push({
      id: `location_start`,
      header: intl.formatMessage({ id: `routes.excel.columns.location_start` }),
      formatter: (driver: Driver) =>
        driver.start_location ? `${driver.start_location?.lat}, ${driver.start_location?.lng}` : ``,
    });

    formatters.push({
      id: `address_start`,
      header: intl.formatMessage({ id: `routes.excel.columns.address_start` }),
      formatter: (driver: Driver) => driver.start_location?.label ?? ``,
    });
  }

  if (parsedTableColumns.end_location) {
    formatters.push({
      id: `location_end`,
      header: intl.formatMessage({ id: `routes.excel.columns.location_end` }),
      formatter: (driver: Driver) =>
        driver.end_location ? `${driver.end_location?.lat}, ${driver.end_location?.lng}` : ``,
    });

    formatters.push({
      id: `address_end`,
      header: intl.formatMessage({ id: `routes.excel.columns.address_end` }),
      formatter: (driver: Driver) => driver.end_location?.label ?? ``,
    });
  }

  if (parsedTableColumns.time_window) {
    formatters.push({
      id: `time_window`,
      header: intl.formatMessage({ id: `routes.excel.columns.time_window` }),
      formatter: (driver: Driver) => (driver.time_window ? parseTimewindowToExcel(driver.time_window) : ``),
    });
  }

  if (parsedTableColumns.status) {
    formatters.push({
      id: `status`,
      header: intl.formatMessage({ id: `routes.excel.columns.status` }),
      formatter: (driver: Driver) =>
        driver.status
          ? intl.formatMessage({
              id: `routes.status.${driver.status}`,
            })
          : undefined,
    });
  }

  if (parsedTableColumns.stops) {
    formatters.push({
      id: `stops`,
      header: intl.formatMessage({ id: `routes.excel.columns.stops` }),
      formatter: (driver: Driver) => driver.stops.length,
      type: `n`,
    });
  }

  if (parsedTableColumns.max_services) {
    formatters.push({
      id: `max_services`,
      header: intl.formatMessage({ id: `routes.excel.columns.max_services` }),
      formatter: (driver: Driver) => driver.max_services,
      type: `n`,
    });
  }

  if (parsedTableColumns.duration) {
    formatters.push({
      id: `duration`,
      header: intl.formatMessage({ id: `routes.excel.columns.duration` }),
      formatter: (driver: Driver) => {
        if (driver.planned_end_time === undefined || driver.planned_start_time === undefined) {
          return ``;
        }
        return `${parseSecondsToHMText(driver.planned_end_time - driver.planned_start_time)}`;
      },
      type: `n`,
    });
  }

  if (parsedTableColumns.max_distance) {
    formatters.push({
      id: `max_distance`,
      header: `${intl.formatMessage({ id: `routes.excel.columns.max_distance` })} (${distanceIn})`,
      formatter: (driver: Driver) => {
        if (driver.max_distance === undefined) {
          return undefined;
        }
        return kmToSM(driver.max_distance / 1000, distanceIn).toFixed(1);
      },
      type: `n`,
    });
  }

  if (parsedTableColumns.min_distance) {
    formatters.push({
      id: `min_distance`,
      header: `${intl.formatMessage({ id: `routes.excel.columns.min_distance` })} (${distanceIn})`,
      formatter: (driver: Driver) => {
        if (driver.min_distance === undefined) {
          return undefined;
        }
        return kmToSM(driver.min_distance / 1000, distanceIn).toFixed(1);
      },
      type: `n`,
    });
  }

  if (parsedTableColumns.total_distance) {
    formatters.push({
      id: `total_distance`,
      header: intl.formatMessage({ id: `routes.excel.columns.total_distance` }),
      formatter: (driver: Driver) => driver.getTotalDistanceWithUnits(project),
    });
  }

  if (parsedTableColumns.distance_traveled) {
    formatters.push({
      id: `distance_traveled`,
      header: intl.formatMessage({ id: `routes.excel.columns.distance_traveled` }),
      formatter: (driver: Driver) => driver.distance_traveled,
    });
  }

  if (parsedTableColumns.max_time) {
    formatters.push({
      id: `max_time`,
      header: intl.formatMessage({ id: `routes.excel.columns.max_time` }),
      formatter: (driver: Driver) => {
        if (!driver.max_time) {
          return ``;
        }
        return `${driver.max_time / 60} min`;
      },
    });
  }

  if (showRootParams && parsedTableColumns.min_time) {
    formatters.push({
      id: `min_time`,
      header: intl.formatMessage({ id: `routes.excel.columns.min_time` }),
      formatter: (driver: Driver) => {
        if (!driver.min_time) {
          return ``;
        }
        return `${driver.min_time / 60} min`;
      },
    });
  }

  if (parsedTableColumns.assigned_weight) {
    formatters.push({
      id: `assigned_weight`,
      header: intl.formatMessage({ id: `routes.excel.columns.assigned_weight` }),
      formatter: (driver: Driver) => `${Math.round(driver.getAssignedWeight() * 10) / 10} (${weightIn})`,
      type: `n`,
    });
  }

  if (parsedTableColumns.max_weight) {
    formatters.push({
      id: `max_weight`,
      header: `${intl.formatMessage({ id: `routes.excel.columns.max_weight` })} (${weightIn})`,
      formatter: (driver: Driver) => driver.max_weight,
      type: `n`,
    });
  }

  if (parsedTableColumns.assigned_volume) {
    formatters.push({
      id: `assigned_volume`,
      header: `${intl.formatMessage({ id: `routes.excel.columns.assigned_volume` })} (${volumeIn})`,
      formatter: (driver: Driver) => Math.round(driver.getAssignedVolume() * 10) / 10,
      type: `n`,
    });
  }

  if (parsedTableColumns.max_volume) {
    formatters.push({
      id: `max_volume`,
      header: `${intl.formatMessage({ id: `routes.excel.columns.max_volume` })} (${volumeIn})`,
      formatter: (driver: Driver) => driver.max_volume,
      type: `n`,
    });
  }

  if (parsedTableColumns.provides) {
    formatters.push({
      id: `provides`,
      header: intl.formatMessage({ id: `routes.excel.columns.provides` }),
      formatter: (driver: Driver) => parseStringArray(driver.provides),
    });
  }

  if (parsedTableColumns.plate) {
    formatters.push({
      id: `plate`,
      header: intl.formatMessage({ id: `routes.excel.columns.plate` }),
      formatter: (driver: Driver) => driver.plate,
    });
  }

  if (parsedTableColumns.comments) {
    formatters.push({
      id: `comments`,
      header: intl.formatMessage({ id: `routes.excel.columns.comments` }),
      formatter: (driver: Driver) => driver.comments,
    });
  }

  if (parsedTableColumns.phone) {
    formatters.push({
      id: `phone`,
      header: intl.formatMessage({ id: `routes.excel.columns.phone` }),
      formatter: (driver: Driver) => driver.phone,
    });
  }

  if (parsedTableColumns.email) {
    formatters.push({
      id: `email`,
      header: intl.formatMessage({ id: `routes.excel.columns.email` }),
      formatter: (driver: Driver) => driver.email,
    });
  }

  // if (parsedTableColumns.url) {
  //   formatters.push({
  //     id: `url`,
  //     header: intl.formatMessage({ id: `routes.excel.columns.url` }),
  //     formatter: (driver: Driver) => driver.url,
  //   });
  // }

  if (parsedTableColumns.total_price) {
    formatters.push({
      id: `price_per_minute`,
      header: intl.formatMessage({ id: `routes.excel.columns.price_per_minute` }),
      formatter: (driver: Driver) => driver.price_per_minute,
    });
    formatters.push({
      id: `price_per_distance`,
      header: intl.formatMessage({ id: `routes.excel.columns.price_per_distance` }),
      formatter: (driver: Driver) => driver.price_per_distance,
    });

    formatters.push({
      id: `total_price`,
      header: intl.formatMessage({ id: `routes.excel.columns.total_price` }),
      formatter: (driver: Driver) => driver.getTotalPriceWithUnits(project),
    });
  }

  if (parsedTableColumns.start_date) {
    formatters.push({
      id: `start_date`,
      header: intl.formatMessage({ id: `routes.excel.columns.start_date` }),
      formatter: (driver: Driver) => dateFormat(driver.start_date),
      type: `d`,
    });
  }

  if (parsedTableColumns.planned_start_time) {
    formatters.push({
      id: `planned_start_time`,
      header: intl.formatMessage({ id: `routes.excel.columns.planned_start_time` }),
      formatter: (driver: Driver) => parseSecondsToHM(driver.planned_start_time),
      type: `n`,
    });
  }

  if (parsedTableColumns.end_date) {
    formatters.push({
      id: `end_date`,
      header: intl.formatMessage({ id: `routes.excel.columns.end_date` }),
      formatter: (driver: Driver) => dateFormat(driver.end_date),
      type: `d`,
    });
  }

  if (parsedTableColumns.planned_end_time) {
    formatters.push({
      id: `planned_end_time`,
      header: intl.formatMessage({ id: `routes.excel.columns.planned_end_time` }),
      formatter: (driver: Driver) => parseSecondsToHM(driver.planned_end_time),
      type: `n`,
    });
  }

  formatters.push({
    id: `route_link`,
    header: intl.formatMessage({ id: `routes.excel.columns.route_link` }),
    formatter: (driver: Driver) => driver.getRouteMagicLink(),
    type: `l`,
  });

  if (parsedTableColumns.survey) {
    formatters.push({
      header: intl.formatMessage({ id: `routes.excel.columns.survey` }),
      id: `survey`,
      formatter: (driver: Driver) => {
        if (!driver.survey) {
          return ``;
        }
        return `${driver.survey?.rating ?? 0}/5`;
      },
    });
  }

  if (parsedTableColumns.created_at) {
    formatters.push({
      id: `created_at`,
      header: intl.formatMessage({ id: `routes.excel.columns.created_at` }),
      formatter: (driver: Driver) => dateFormat(driver.created_at),
      type: `d`,
    });
  }

  return formatters;
};

export const exportDrivers = ({
  showRootParams = false,
  drivers,
  project,
  tableColumns,
}: {
  showRootParams?: boolean;
  drivers: Driver[];
  project: Project;
  tableColumns?: Array<DriversTableColumn>;
}) => {
  const customFields: CustomFieldDefinition[] = project.custom_fields?.vehicle ?? [];

  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: `Drivers`,
    Subject: `Drivers exported`,
    Author: `Routal`,
    CreatedDate: new Date(),
  };

  const parsedTableColumns = (tableColumns ?? []).reduce(
    (amount, next) => ({
      ...amount,
      [next.identifier]: next.displayable,
    }),
    {} as Record<DriversTableColumnKeys, boolean>
  );

  const driverCustomFieldFormatter = customFields
    .filter(
      (cf) =>
        cf.field_model === `vehicle` &&
        cf.enabled &&
        (parsedTableColumns as any)[`${CUSTOM_FIELD_COLUMN_ID_PREFIX}${cf.custom_id}`] === true
    )
    .map((cf) => {
      const formatter = customFieldToFormatter(cf);
      return {
        ...formatter,
        formatter: (driver: Driver) => formatter.formatter && formatter.formatter(driver.custom_fields),
      };
    });

  const allDriversFormatter = [
    ...getDriverFormatters({ showRootParams, parsedTableColumns, project }),
    ...driverCustomFieldFormatter,
  ];

  const driversSheet = jsonToExcel(drivers, allDriversFormatter);

  const driversLabel = intl.formatMessage({ id: `routes.excel.sheets.drivers` });
  wb.SheetNames.push(driversLabel);
  wb.Sheets[driversLabel] = driversSheet;

  return XLSX.write(wb, { bookType: `xlsx`, type: `binary`, cellDates: true });
};
